import React, { useState, useEffect, lazy, Suspense, useCallback, Component, type ReactNode, type ErrorInfo } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { onAuthStateChanged, User as FirebaseUser, setPersistence, browserLocalPersistence } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { auth, db } from './config/firebase';
import {
  adminBaseUrl,
  isAdminAuthHost,
  isAdminProductionHost,
  isLegacyDashboardHost,
  isWeb2posPublicAuthPath,
  isWeb2posUtilityPath,
} from './config/adminHost';
import { consumeGoogleRedirectResult } from './utils/dashboardGoogleAuth';
import { Restaurant, User, RoleLabels } from './types';
import { isReceiptOnlyWebOwner } from './utils/restaurantProductAccess';
import {
  adminAppRoutePath,
  adminDeepLinkPageFromPath,
  forceDashboardUrl,
  isHqDashboardRole,
} from './utils/adminAppRoutePath';
import {
  pageTypeForRestaurantAdminSub,
  parseRestaurantAdminSubRoute,
  restaurantAdminSubPath,
  stashRestaurantSubPageSnapshot,
} from './utils/restaurantAdminSubRoutes';
import { fetchFullRestaurant } from './utils/restaurantListFetch';
import { withTimeout } from './utils/asyncWithTimeout';
import AdminAlertsPopup from './components/AdminAlertsPopup';
// Auth + Web2POS — lazy: /order·/home 손님 번들에서 제외(메인 JS 전송량·파싱 시간 단축)
import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';

const TheZoneReceiptAdminPage = lazy(
  () =>
    import(
      /* webpackChunkName: "thezone-receipt-admin-page" */ './pages/admin/TheZoneReceiptAdminPage'
    ),
);
const Web2posUploadPage = lazy(() => import('./pages/web2pos/Web2posUploadPage'));
const Web2posDownloadPage = lazy(() => import('./pages/web2pos/Web2posDownloadPage'));

// Admin / 백오피스 / 공개 랜딩 — lazy: /order 첫 로드 시 메인 번들에 관리자 코드 포함 방지
const DashboardPage = lazy(() => import('./pages/admin/DashboardPage'));
const RestaurantsPage = lazy(() => import('./pages/admin/RestaurantsPage'));
const RestaurantCustomerDirectoryPage = lazy(
  () => import('./pages/admin/RestaurantCustomerDirectoryPage'),
);
const RestaurantTabsPage = lazy(() => import('./pages/admin/RestaurantTabsPage'));
const UsersPage = lazy(() => import('./pages/admin/UsersPage'));
const RestaurantSettingsPage = lazy(() => import('./pages/admin/RestaurantSettingsPage'));
const CustomerPortalPage = lazy(() => import('./pages/customer/CustomerPortalPage'));
const ReceiptOwnerWebPage = lazy(() => import('./pages/customer/ReceiptOwnerWebPage'));
const RestaurantActivePage = lazy(() => import('./pages/admin/RestaurantActivePage'));
const BillingPage = lazy(() => import('./pages/admin/BillingPage'));
const QuotesContractsPage = lazy(
  () =>
    import(
      /* webpackChunkName: "quotes-contracts-page", webpackPrefetch: true */ './pages/admin/QuotesContractsPage'
    ),
);
const ContractSignPage = lazy(() => import('./pages/public/ContractSignPage'));

const RestaurantHomePage = lazy(() => import('./pages/restaurant/RestaurantHomePage'));

const ResetPasswordPage = lazy(() => import('./pages/auth/ResetPasswordPage'));

const BackOfficeLayout = lazy(() => import('./components/layout/BackOfficeLayout'));
const BasicInfoPage = lazy(() => import('./pages/backoffice/BasicInfoPage'));
const MenuListPage = lazy(() => import('./pages/backoffice/MenuListPage'));
const MenuEditPage = lazy(() => import('./pages/backoffice/MenuEditPage'));
const TableMapManagerPage = lazy(() => import('./pages/backoffice/TableMapManagerPage'));
const TableMapPage = lazy(() => import('./pages/backoffice/TableMapPage'));
const EmployeeManagerPage = lazy(() => import('./pages/backoffice/EmployeeManagerPage'));
const EmployeeInfoPage = lazy(() => import('./pages/backoffice/EmployeeInfoPage'));
const OrderSetupPage = lazy(() => import('./pages/backoffice/OrderSetupPage'));
const TableReservationSettingsPage = lazy(() => import('./pages/backoffice/TableReservationSettingsPage'));
const TogoSettingsPage = lazy(() => import('./pages/backoffice/TogoSettingsPage'));
const WorkSchedulePage = lazy(() => import('./pages/backoffice/WorkSchedulePage'));
const ShiftSwapsPage = lazy(() => import('./pages/backoffice/ShiftSwapsPage'));
const TimeOffRequestPage = lazy(() => import('./pages/backoffice/TimeOffRequestPage'));
const EmployeeReportPage = lazy(() => import('./pages/backoffice/EmployeeReportPage'));
const PayrollSettingPage = lazy(() => import('./pages/backoffice/PayrollSettingPage'));
const PrinterPage = lazy(() => import('./pages/backoffice/PrinterPage'));
const CreditCardReaderPage = lazy(() => import('./pages/backoffice/CreditCardReaderPage'));
const TableDevicesPage = lazy(() => import('./pages/backoffice/TableDevicesPage'));
const QrCodePage = lazy(() => import('./pages/backoffice/QrCodePage'));
const KdsPage = lazy(() => import('./pages/backoffice/KdsPage'));
const HardwareManagerPage = lazy(() => import('./pages/backoffice/HardwareManagerPage'));
const ReportManagerPage = lazy(() => import('./pages/backoffice/ReportManagerPage'));
const RestaurantReportsPage = lazy(() => import('./pages/backoffice/RestaurantReportsPage'));
const NewReportPage = lazy(() => import('./pages/backoffice/NewReportPage'));
const GroupReportPage = lazy(() => import('./pages/backoffice/GroupReportPage'));
const SalesReportPage = lazy(() => import('./pages/backoffice/SalesReportPage'));
const PaymentReportPage = lazy(() => import('./pages/backoffice/PaymentReportPage'));
const MenuAnalysisPage = lazy(() => import('./pages/backoffice/MenuAnalysisPage'));
const InventoryReportPage = lazy(() => import('./pages/backoffice/InventoryReportPage'));
const CustomerReportPage = lazy(() => import('./pages/backoffice/CustomerReportPage'));
const EmployeePerformancePage = lazy(() => import('./pages/backoffice/EmployeePerformancePage'));
const OperationalReportPage = lazy(() => import('./pages/backoffice/OperationalReportPage'));
const PaymentSettingsPage = lazy(() => import('./pages/backoffice/PaymentSettingsPage'));

const PublicLandingPage = lazy(() => import('./pages/public/PublicLandingPage'));

// 온라인 주문 — `pages/online-order` 전용 폴더(구현) / `pages/order/OrderPage` 는 동일 모듈 re-export
// index.tsx에서 /order/* 일 때 동적 import로 청크 병렬 prefetch
const OrderPage = lazy(() =>
  import(/* webpackChunkName: "online-order-page", webpackPrefetch: true */ './pages/online-order/OnlineOrderPage')
);

/** Activate 등 lazy 페이지 렌더 크래시 시 빈 화면 대신 안내 */
class AdminLazyPageErrorBoundary extends Component<
  { children: ReactNode; onBack: () => void; title: string },
  { error: Error | null }
> {
  state: { error: Error | null } = { error: null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[AdminLazyPageErrorBoundary]', this.props.title, error, info);
  }

  render() {
    if (this.state.error) {
      const isChunk =
        this.state.error.name === 'ChunkLoadError' ||
        /Loading chunk [\d]+ failed/i.test(this.state.error.message);
      return (
        <div style={{ minHeight: '100vh', background: '#f1f5f9', padding: 24 }}>
          <p style={{ color: '#dc2626', fontWeight: 700, fontSize: 16 }}>
            {this.props.title} could not load.
          </p>
          <p style={{ color: '#64748b', fontSize: 14, marginTop: 8 }}>
            {isChunk
              ? 'The app was updated while this tab was open. Reload once to fetch the latest scripts.'
              : this.state.error.message}
          </p>
          <div style={{ display: 'flex', gap: 8, marginTop: 16, flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={() => window.location.reload()}
              style={{
                padding: '10px 18px',
                background: '#f97316',
                color: '#fff',
                border: 'none',
                borderRadius: 8,
                cursor: 'pointer',
                fontWeight: 700,
              }}
            >
              Reload page
            </button>
            <button
              type="button"
              onClick={() => {
                this.setState({ error: null });
                this.props.onBack();
              }}
              style={{
                padding: '10px 18px',
                background: '#475569',
                color: '#fff',
                border: 'none',
                borderRadius: 8,
                cursor: 'pointer',
                fontWeight: 600,
              }}
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

class ActivatePageErrorBoundary extends Component<
  { children: ReactNode; onBack: () => void },
  { error: Error | null }
> {
  state: { error: Error | null } = { error: null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[ActivatePageErrorBoundary]', error, info);
  }

  render() {
    if (this.state.error) {
      return (
        <div style={{ minHeight: '100vh', background: '#f8fafc', padding: 24 }}>
          <p style={{ color: '#dc2626', fontWeight: 600 }}>
            Activate page failed to render.
          </p>
          <p style={{ color: '#64748b', fontSize: 14 }}>{this.state.error.message}</p>
          <button
            type="button"
            onClick={() => {
              this.setState({ error: null });
              this.props.onBack();
            }}
            style={{
              marginTop: 12,
              padding: '8px 16px',
              background: '#475569',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              cursor: 'pointer',
            }}
          >
            ← Restaurants
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// 페이지 타입
type PageType = 
  | 'login' 
  | 'register' 
  | 'dashboard' 
  | 'customer-portal'
  | 'restaurants' 
  | 'restaurant-settings'
  | 'restaurant-active'
  | 'deleted-restaurants'
  | 'menu-manage'
  | 'users'
  | 'order-page'
  | 'restaurant-home'
  | 'reset-password'
  | 'restaurant-report'
  | 'group-report'
  | 'billing'
  | 'quotes-contracts'
  | 'thezone-receipt'
  | 'customer-directory';

/** Deleted Restaurants URL에서 Active/Report 등으로 이동할 때만 switch 사용 */
const DELETED_RESTAURANTS_SUB_PAGES: PageType[] = [
  'restaurant-active',
  'restaurant-report',
  'restaurant-settings',
  'menu-manage',
];

export function OfflineBanner() {
  const [offline, setOffline] = useState(!navigator.onLine);
  useEffect(() => {
    const goOff = () => setOffline(true);
    const goOn = () => { setOffline(false); window.location.reload(); };
    window.addEventListener('offline', goOff);
    window.addEventListener('online', goOn);
    return () => { window.removeEventListener('offline', goOff); window.removeEventListener('online', goOn); };
  }, []);
  if (!offline) return null;
  return (
    <div style={{position:'fixed',top:0,left:0,right:0,zIndex:100000,background:'#dc2626',color:'#fff',textAlign:'center',padding:'10px 16px',fontSize:14,fontWeight:600}}>
      No internet connection. The page will reload automatically when you're back online.
    </div>
  );
}

/** 온라인 주문 청크 로드 중 — 대시보드 전역 로딩과 동일한 스타일(첫 화면 체감 통일) */
function OrderRouteLoadingFallback() {
  return (
    <div
      style={{
        minHeight: '100vh',
        backgroundColor: '#0f172a',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <div style={{ textAlign: 'center' }}>
        <div
          style={{
            width: '48px',
            height: '48px',
            border: '4px solid #334155',
            borderTop: '4px solid #f97316',
            borderRadius: '50%',
            animation: 'orderRouteSpin 1s linear infinite',
            margin: '0 auto 16px',
          }}
        />
        <p style={{ color: '#94a3b8', fontSize: 15, margin: 0 }}>Loading…</p>
      </div>
      <style>{`
        @keyframes orderRouteSpin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

function App() {
  // React Router hooks must be called at the top level
  const location = useLocation();
  const navigate = useNavigate();
  
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState<PageType>('login');
  const [dashboardAuthReady, setDashboardAuthReady] = useState(false);
  const [selectedRestaurant, setSelectedRestaurant] = useState<Restaurant | null>(null);
  const [selectedRestaurantId, setSelectedRestaurantId] = useState<string>('');
  const [groupReportIds, setGroupReportIds] = useState<string[]>([]);
  const [groupReportName, setGroupReportName] = useState<string>('');
  const [reportReturnPage, setReportReturnPage] = useState<PageType>('restaurants');
  const [orderSlug, setOrderSlug] = useState<string>('');
  const [isOrderPage, setIsOrderPage] = useState(false);
  const [isHomePage, setIsHomePage] = useState(false);
  const [isResetPasswordPage, setIsResetPasswordPage] = useState(false);

  const handleOpenActive = useCallback(
    (restaurant: Restaurant) => {
      if (!restaurant?.id) {
        console.warn('[handleOpenActive] missing restaurant.id', restaurant);
        return;
      }
      const id = String(restaurant.id);
      stashRestaurantSubPageSnapshot({ ...restaurant, id });
      setSelectedRestaurant({ ...restaurant, id });
      setSelectedRestaurantId(id);
      setCurrentPage('restaurant-active');
      navigate(restaurantAdminSubPath(id, 'active'));
    },
    [navigate],
  );

  const handleRestaurantLifecycleUpdated = useCallback((r: Restaurant) => {
    if (!r?.id) return;
    setSelectedRestaurant({ ...r, id: String(r.id) });
  }, []);

  const handleRestaurantSoftDeleted = useCallback((r: Restaurant) => {
    if (!r?.id) return;
    setSelectedRestaurant({ ...r, id: String(r.id) });
    setCurrentPage('restaurants');
    navigate('/deleted-restaurants');
  }, [navigate]);

  /** 레스토랑 서브화면 → Back: 전체 목록 + 해당 레스토랑 Quick Actions 모달 */
  const handleBackToRestaurantModal = useCallback(
    (restaurantId?: string) => {
      const id = restaurantId || selectedRestaurant?.id || selectedRestaurantId;
      setCurrentPage('restaurants');
      if (id) {
        navigate(`/restaurants?openModal=${encodeURIComponent(String(id))}`, { replace: true });
      } else {
        navigate('/restaurants', { replace: true });
      }
    },
    [navigate, selectedRestaurant?.id, selectedRestaurantId],
  );

  const handleOpenReport = useCallback(
    (restaurantId: string, returnPage: PageType = 'restaurants') => {
      const id = String(restaurantId).trim();
      if (!id) return;
      stashRestaurantSubPageSnapshot({ id, name: 'Restaurant' });
      setSelectedRestaurantId(id);
      setReportReturnPage(returnPage);
      setCurrentPage('restaurant-report');
      navigate(restaurantAdminSubPath(id, 'report'));
    },
    [navigate],
  );

  // React Router 기반 라우트 체크 (/dashboard/* hosting prefix stripped for app routes)
  const normalizedPath = adminAppRoutePath(location.pathname);
  const isBackOfficeRoute = location.pathname.startsWith('/restaurant/') && location.pathname.includes('/backoffice');
  const isNewReportRoute = location.pathname.startsWith('/restaurant/') && location.pathname.endsWith('/new-report');
  const isOrderRoute = location.pathname.startsWith('/order/');
  const isHomeRoute = location.pathname.startsWith('/home/');
  const isDashboardPath = location.pathname === '/dashboard' || location.pathname.startsWith('/dashboard/');
  const isQuotesPath = normalizedPath === '/quotes';
  const isReceiptAdminPath = normalizedPath === '/thezone-receipt';
  const isDeletedRestaurantsPath = normalizedPath === '/deleted-restaurants';
  const restaurantAdminSubRoute = parseRestaurantAdminSubRoute(location.pathname);
  const isRestaurantsAdminPath =
    normalizedPath === '/restaurants' || normalizedPath.startsWith('/restaurants/');
  const isLoginPath = normalizedPath === '/login';
  const isRegisterPath = normalizedPath === '/register';
  const isContractSignRoute = /^\/sign\/[^/]+/.test(location.pathname);
  const hostname = window.location.hostname;
  const isAdminHost = isAdminProductionHost(hostname);
  const isLegacyDashboard = isLegacyDashboardHost(hostname);
  const isAdminAuth = isAdminAuthHost(hostname);
  const isFirebaseDefaultHost =
    hostname.endsWith('.web.app') || hostname.endsWith('.firebaseapp.com');
  const isLocalWeb2posDev =
    (hostname === 'localhost' || hostname === '127.0.0.1') &&
    isWeb2posUtilityPath(location.pathname);
  /** web2pos.com bare `/` only — do not treat /dashboard (maps to normalized `/`) as site root. */
  const isWeb2posSiteRoot =
    isAdminHost &&
    (location.pathname === '/' || location.pathname === '');
  const isWeb2posUtilityRoute =
    isAdminHost && isWeb2posUtilityPath(location.pathname);
  const isAdminArea =
    isAdminHost ||
    isDashboardPath ||
    isQuotesPath ||
    isReceiptAdminPath ||
    isDeletedRestaurantsPath ||
    isRestaurantsAdminPath ||
    isBackOfficeRoute ||
    isLocalWeb2posDev;
  // Customer-facing domain (apex). Keep stable until www is fully configured.
  const customerHost = 'thezoneorder.com';

  // 로컬 개발/임시 디버깅 시에는 강제 도메인 정규화(redirect)를 끈다.
  // - localhost에서 대시보드를 보거나
  // - 커스텀 도메인 연결이 완료되기 전 web.app에서 확인이 필요할 때 사용
  const bypassDomainRedirect =
    process.env.NODE_ENV === 'development' ||
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    new URLSearchParams(window.location.search).has('noredirect');

  useEffect(() => {
    // Legacy dashboard subdomain → unified admin domain (preserve path)
    if (!bypassDomainRedirect && isLegacyDashboard) {
      const path = `${location.pathname}${location.search}${location.hash}`;
      window.location.replace(`${adminBaseUrl()}${path}`);
      return;
    }

    // 기본 도메인(web.app/firebaseapp.com)으로 접속해도,
    // 손님/관리자 용도에 맞는 커스텀 도메인으로 URL을 정규화한다.
    const path = `${location.pathname}${location.search}${location.hash}`;

    // 주문/홈은 항상 손님 도메인으로
    if (!bypassDomainRedirect && (isOrderRoute || isHomeRoute) && hostname !== customerHost) {
      window.location.replace(`https://${customerHost}${path}`);
      return;
    }

    // 관리자 도메인에서 주문/홈으로 들어오면 손님 도메인으로 보내기
    if (!bypassDomainRedirect && isAdminAuth && (isOrderRoute || isHomeRoute)) {
      window.location.replace(`https://${customerHost}${path}`);
      return;
    }

    // Firebase 기본 도메인으로 들어오면 관리자 영역은 web2pos.com으로
    // (web2pos.web.app은 제외 - web2pos 전용 사이트)
    if (!bypassDomainRedirect && isFirebaseDefaultHost && hostname !== 'web2pos.web.app' && !isOrderRoute && !isHomeRoute) {
      const nextPath = location.pathname === '/' ? '/dashboard' : location.pathname;
      window.location.replace(`${adminBaseUrl()}${nextPath}${location.search}${location.hash}`);
    }
  }, [
    bypassDomainRedirect,
    isAdminAuth,
    isLegacyDashboard,
    isFirebaseDefaultHost,
    isHomeRoute,
    isOrderRoute,
    hostname,
    location.hash,
    location.pathname,
    location.search,
  ]);

  useEffect(() => {
    if (isLegacyDashboard && location.pathname === '/') {
      window.history.replaceState({}, '', '/dashboard');
    }
  }, [isLegacyDashboard, location.pathname]);

  /** /restaurants/* sub-routes → load restaurant + in-app page */
  useEffect(() => {
    if (!firebaseUser || !currentUser) return;
    const sub = parseRestaurantAdminSubRoute(location.pathname);
    if (!sub) return;
    const page = pageTypeForRestaurantAdminSub(sub.sub);
    setSelectedRestaurantId(sub.restaurantId);
    setCurrentPage(page);
    if (sub.sub === 'report') {
      setReportReturnPage('restaurants');
    }
    let cancelled = false;
    void (async () => {
      try {
        const full = await fetchFullRestaurant(sub.restaurantId);
        if (cancelled) return;
        if (full) {
          setSelectedRestaurant({ ...full, id: String(full.id) });
        }
      } catch {
        /* keep list snapshot if any */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [firebaseUser, currentUser, location.pathname]);

  /** /restaurants URL(Back Office Back 등) → RestaurantsPage + openModal */
  useEffect(() => {
    if (!firebaseUser || !currentUser) return;
    const normalized = location.pathname.replace(/\/$/, '') || '/';
    if (normalized !== '/restaurants') return;
    setCurrentPage((prev) => {
      const keepCurrent: PageType[] = [
        'menu-manage',
        'restaurant-settings',
        'restaurant-active',
        'restaurant-report',
        'deleted-restaurants',
      ];
      if (keepCurrent.includes(prev)) return prev;
      return 'restaurants';
    });
  }, [firebaseUser, currentUser, location.pathname]);

  /** Sync URL-backed admin pages (pathname is source of truth; do not depend on currentPage). */
  useEffect(() => {
    const path = adminAppRoutePath(location.pathname);
    const deepLink = adminDeepLinkPageFromPath(location.pathname);
    if (deepLink && (!firebaseUser || !currentUser)) {
      setCurrentPage(deepLink);
      return;
    }
    if (!firebaseUser || !currentUser) return;
    if (path === '/quotes') {
      setCurrentPage('quotes-contracts');
    } else if (path === '/thezone-receipt') {
      setCurrentPage('thezone-receipt');
    } else if (path === '/deleted-restaurants') {
      setCurrentPage('restaurants');
    } else if (path === '/restaurants/customer-directory') {
      setCurrentPage('customer-directory');
    } else if (parseRestaurantAdminSubRoute(location.pathname)) {
      const sub = parseRestaurantAdminSubRoute(location.pathname)!;
      setCurrentPage(pageTypeForRestaurantAdminSub(sub.sub));
    } else if (path === '/restaurants') {
      setCurrentPage((prev) =>
        DELETED_RESTAURANTS_SUB_PAGES.includes(prev) ? prev : 'restaurants',
      );
    } else if (path === '/' && isDashboardPath) {
      setCurrentPage('dashboard');
    }
  }, [location.pathname, firebaseUser, currentUser, isDashboardPath]);

  useEffect(() => {
    // 손님용 도메인에서 /dashboard로 접근하면 web2pos.com으로 강제 이동
    if (!bypassDomainRedirect && isDashboardPath && !isAdminHost) {
      const nextUrl = `${adminBaseUrl()}${location.pathname}${location.search}${location.hash}`;
      window.location.replace(nextUrl);
    }
  }, [isDashboardPath, isAdminHost, location.pathname, location.search, location.hash, bypassDomainRedirect]);

  // Persist admin authentication across browser restarts, then process a
  // same-origin Google redirect before subscribing to the auth state.
  useEffect(() => {
    if (!isAdminAuth) {
      setDashboardAuthReady(true);
      return;
    }

    let cancelled = false;
    void (async () => {
      try {
        await withTimeout(setPersistence(auth, browserLocalPersistence), 8000, undefined, 'auth persistence');
        await withTimeout(consumeGoogleRedirectResult(), 12000, { handled: false as const }, 'google redirect');
      } catch (err) {
        console.error('Failed to finish admin Google redirect:', err);
      }
      if (!cancelled) setDashboardAuthReady(true);
    })();

    return () => { cancelled = true; };
  }, [isAdminAuth]);

  useEffect(() => {
    if (!isAdminAuth || dashboardAuthReady) return;
    const t = window.setTimeout(() => setDashboardAuthReady(true), 15000);
    return () => window.clearTimeout(t);
  }, [isAdminAuth, dashboardAuthReady]);

  useEffect(() => {
    if (!loading) return;
    const t = window.setTimeout(() => setLoading(false), 10000);
    return () => window.clearTimeout(t);
  }, [loading]);

  // URL에서 페이지 타입 확인 (가장 먼저!)
  useEffect(() => {
    const path = window.location.pathname;
    const decodedPath = decodeURIComponent(path);
    const searchParams = new URLSearchParams(window.location.search);
    
    // 비밀번호 재설정 페이지 (Firebase action URL 또는 커스텀 URL)
    if (decodedPath === '/reset-password' || 
        (searchParams.get('mode') === 'resetPassword' && searchParams.get('oobCode'))) {
      setIsResetPasswordPage(true);
      setCurrentPage('reset-password');
      return;
    }
    
    // 주문 페이지
    if (decodedPath.startsWith('/order/')) {
      const slug = decodedPath.replace('/order/', '');
      if (slug) {
        setOrderSlug(slug);
        setIsOrderPage(true);
        setCurrentPage('order-page');
      }
    }
    
    // 레스토랑 홈페이지
    if (decodedPath.startsWith('/home/')) {
      const slug = decodedPath.replace('/home/', '');
      if (slug) {
        setOrderSlug(slug);
        setIsHomePage(true);
        setCurrentPage('restaurant-home');
      }
    }

    if (decodedPath === '/quotes') {
      setCurrentPage('quotes-contracts');
    }
    if (decodedPath === '/thezone-receipt') {
      setCurrentPage('thezone-receipt');
    }
    if (decodedPath === '/deleted-restaurants') {
      setCurrentPage('restaurants');
    }
  }, []);

  const resolvePageAfterSignIn = (profile: User, prev: PageType): PageType => {
    const routePath = adminAppRoutePath(window.location.pathname);
    const fromPath = adminDeepLinkPageFromPath(window.location.pathname);
    if (fromPath) return fromPath;
    const subRoute = parseRestaurantAdminSubRoute(window.location.pathname);
    if (subRoute) return pageTypeForRestaurantAdminSub(subRoute.sub);
    const nextParam = new URLSearchParams(window.location.search).get('next');
    if (nextParam && nextParam.startsWith('/')) {
      const fromNext = adminDeepLinkPageFromPath(nextParam);
      if (fromNext) return fromNext;
    }
    const fromAuthEntry =
      routePath === '/login' ||
      routePath === '/register' ||
      routePath === '/' ||
      prev === 'login' ||
      prev === 'register';
    if (fromAuthEntry) {
      forceDashboardUrl();
      return 'dashboard';
    }
    if (isHqDashboardRole(profile.role) && routePath === '/') {
      forceDashboardUrl();
      return 'dashboard';
    }
    return prev;
  };

  // Firestore에서 사용자 정보 가져오기
  const fetchUserData = async (uid: string): Promise<User | null> => {
    try {
      const userDoc = await getDoc(doc(db, 'users', uid));
      if (userDoc.exists()) {
        const data = userDoc.data();
        const user: User = {
          uid: userDoc.id,
          name: data.name || 'User',
          email: data.email || '',
          role: data.role || 'owner',
          restaurantIds: data.restaurantIds || [],
          restaurantRoles: data.restaurantRoles || {},
          salesStaffCode: data.salesStaffCode || undefined,
          isActive: data.isActive !== false,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
        };
        setCurrentUser(user);
        return user;
      }
      const fallback: User = {
        uid,
        name: 'Admin',
        email: firebaseUser?.email || '',
        role: 'admin',
        restaurantIds: [],
        restaurantRoles: {},
        isActive: true,
        createdAt: new Date(),
        updatedAt: new Date(),
      };
      setCurrentUser(fallback);
      return fallback;
    } catch (error) {
      console.error('Error fetching user data:', error);
      let kept: User | null = null;
      setCurrentUser((prev) => {
        if (prev?.uid === uid) {
          kept = prev;
          return prev;
        }
        const stub: User = {
          uid,
          name: 'User',
          email: firebaseUser?.email || '',
          role: 'owner',
          restaurantIds: [],
          restaurantRoles: {},
          isActive: true,
          createdAt: new Date(),
          updatedAt: new Date(),
        };
        kept = stub;
        return stub;
      });
      return kept;
    }
  };

  // 로그인 상태 확인
  useEffect(() => {
    if (isAdminAuth && !dashboardAuthReady) return;

    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setFirebaseUser(user);

      void (async () => {
        try {
          if (user) {
            void withTimeout(
              httpsCallable(getFunctions(undefined, 'us-central1'), 'syncBusinessInfoMembership')(),
              8000,
              undefined,
              'syncBusinessInfoMembership',
            ).catch((error) => {
              console.warn('Business Info membership sync skipped:', error);
            });

            const path = window.location.pathname;
            const sp = new URLSearchParams(window.location.search);
            const deferProfileLoad =
              path.startsWith('/order/') ||
              path.startsWith('/home/') ||
              path === '/reset-password' ||
              (sp.get('mode') === 'resetPassword' && !!sp.get('oobCode'));

            if (deferProfileLoad) {
              void fetchUserData(user.uid);
            } else {
              const profile = await withTimeout(fetchUserData(user.uid), 15000, null, 'fetchUserData');
              if (profile) {
                setCurrentPage((prev) => resolvePageAfterSignIn(profile, prev));
              }
            }
          } else {
            setCurrentUser(null);
          }
        } finally {
          setLoading(false);
        }
      })();
    });

    return () => unsubscribe();
  }, [isAdminAuth, dashboardAuthReady]);

  const normalizedAdminPath = location.pathname.replace(/\/$/, '') || '/';

  // Legacy dashboard host: redirect effect above handles migration; show spinner briefly
  if (isLegacyDashboard && !bypassDomainRedirect) {
    return <OrderRouteLoadingFallback />;
  }

  // 손님 /order, /home: Firebase Auth·프로필 로딩이 끝날 때까지 전역 스피너에 막히지 않도록 최우선 렌더
  // (첫 페인트 수십 초 단축 — 기존에는 onAuthStateChanged 대기 후에만 Order 청크가 마운트됨)
  const pathForCustomer = decodeURIComponent(location.pathname).replace(/\/$/, '') || '/';
  const orderSlugEarly =
    pathForCustomer.startsWith('/order/') ? pathForCustomer.slice('/order/'.length) : '';
  const homeSlugEarly =
    pathForCustomer.startsWith('/home/') ? pathForCustomer.slice('/home/'.length) : '';

  if (orderSlugEarly) {
    return (
      <Suspense fallback={<OrderRouteLoadingFallback />}>
        <OrderPage
          restaurantSlug={orderSlugEarly}
          onBack={() => {
            window.history.pushState({}, '', '/');
            setIsOrderPage(false);
            setCurrentPage(firebaseUser ? 'dashboard' : 'login');
          }}
        />
      </Suspense>
    );
  }
  if (homeSlugEarly) {
    return (
      <Suspense fallback={<OrderRouteLoadingFallback />}>
        <RestaurantHomePage restaurantSlug={homeSlugEarly} />
      </Suspense>
    );
  }

  const isAuthEntryPath = isLoginPath || isRegisterPath;

  // 로딩 중일 때 (login/register는 바로 폼 표시)
  if (loading && !isAuthEntryPath) {
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: '#0f172a',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '48px',
            height: '48px',
            border: '4px solid #334155',
            borderTop: '4px solid #f97316',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }}></div>
          <p style={{ color: '#94a3b8' }}>Loading...</p>
        </div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  // 비밀번호 재설정 페이지 (로그인 여부 상관없이 표시)
  const currentPath = window.location.pathname;
  const currentSearch = new URLSearchParams(window.location.search);
  const isResetPage = currentPath === '/reset-password' || 
    (currentSearch.get('mode') === 'resetPassword' && currentSearch.get('oobCode'));
  
  if (isResetPage || isResetPasswordPage) {
    return (
      <Suspense fallback={<OrderRouteLoadingFallback />}>
        <ResetPasswordPage />
      </Suspense>
    );
  }

  if (isContractSignRoute) {
    return (
      <Suspense fallback={<OrderRouteLoadingFallback />}>
        <Routes>
          <Route path="/sign/:token" element={<ContractSignPage />} />
        </Routes>
      </Suspense>
    );
  }

  // 공개 영역(랜딩) - 누구나 접근 가능
  // - /order/*, /home/* 는 위에서 이미 처리
  // - web2pos.com 은 로그인 후 Dashboard / Downloads / Upload
  // - 관리자 영역(대시보드/백오피스)은 아래에서 로그인 요구
  if (
    !isAdminArea &&
    !isOrderRoute &&
    !isHomeRoute &&
    !isWeb2posPublicAuthPath(normalizedPath)
  ) {
    return (
      <Suspense fallback={<OrderRouteLoadingFallback />}>
        <PublicLandingPage
          onGoToDashboard={() => {
            window.location.assign('/dashboard');
          }}
        />
      </Suspense>
    );
  }

  // new-report 라우트 (로그인 불필요)
  if (isNewReportRoute) {
    return (
      <Suspense fallback={<OrderRouteLoadingFallback />}>
        <Routes>
          <Route path="/restaurant/:restaurantId/new-report" element={<NewReportPage />} />
        </Routes>
      </Suspense>
    );
  }

  // 백오피스 라우트 (로그인 필요)
  if (isBackOfficeRoute) {
    // pos-reports, reports는 로그인 없이 접근 가능
    const isPosReportsRoute = location.pathname.endsWith('/pos-reports');
    const isReportsRoute = location.pathname.endsWith('/reports');
    if (!isPosReportsRoute && !isReportsRoute && (!firebaseUser || !currentUser)) {
      return <LoginPage onGoToRegister={() => setCurrentPage('register')} />;
    }
    return (
      <Suspense fallback={<OrderRouteLoadingFallback />}>
        <Routes>
          <Route path="/restaurant/:restaurantId/backoffice" element={<BackOfficeLayout />}>
            <Route index element={<Navigate to="menu" replace />} />
            <Route path="basic-info" element={<BasicInfoPage />} />
            <Route path="menu" element={<MenuListPage />} />
            <Route path="menu/edit/:menuId" element={<MenuEditPage />} />
            <Route path="tables" element={<TableMapManagerPage />} />
            <Route path="table-map" element={<TableMapPage />} />
            <Route path="table-reservation-settings" element={<TableReservationSettingsPage />} />
            <Route path="order-setup" element={<OrderSetupPage />} />
            <Route path="togo-settings" element={<TogoSettingsPage />} />
            <Route path="employees" element={<EmployeeManagerPage />} />
            <Route path="employee-info" element={<EmployeeInfoPage />} />
            <Route path="work-schedule" element={<WorkSchedulePage />} />
            <Route path="shift-swaps" element={<ShiftSwapsPage />} />
            <Route path="time-off-request" element={<TimeOffRequestPage />} />
            <Route path="employee-report" element={<EmployeeReportPage />} />
            <Route path="payroll-setting" element={<PayrollSettingPage />} />
            <Route path="hardware" element={<HardwareManagerPage />} />
            <Route path="printer" element={<PrinterPage />} />
            <Route path="credit-card-reader" element={<CreditCardReaderPage />} />
            <Route path="table-devices" element={<TableDevicesPage />} />
            <Route path="qr-code" element={<QrCodePage />} />
            <Route path="kds" element={<KdsPage />} />
            <Route path="reports" element={<ReportManagerPage />} />
            <Route path="pos-reports" element={<RestaurantReportsPage />} />
            <Route path="sales-report" element={<SalesReportPage />} />
            <Route path="inventory-report" element={<InventoryReportPage />} />
            <Route path="customer-report" element={<CustomerReportPage />} />
            <Route path="employee-performance" element={<EmployeePerformancePage />} />
            <Route path="menu-analysis" element={<MenuAnalysisPage />} />
            <Route path="payment-report" element={<PaymentReportPage />} />
            <Route path="operational-report" element={<OperationalReportPage />} />
            <Route path="payment-settings" element={<PaymentSettingsPage />} />
          </Route>
        </Routes>
      </Suspense>
    );
  }

  // 고객용 /order·/home 은 위에서(Auth 전) 처리함

  // 로그인 안된 상태
  if (!firebaseUser || !currentUser) {
    // Admin 영역으로 직접 접근하면 로그인 URL로 정규화
    if (isAdminArea && !isLoginPath && !isRegisterPath) {
      const next = `${location.pathname}${location.search}${location.hash}`;
      return <Navigate to={`/login?next=${encodeURIComponent(next)}`} replace />;
    }

    if (isRegisterPath || currentPage === 'register') {
      return (
        <RegisterPage
          onGoToLogin={() => {
            window.history.pushState({}, '', '/login');
            setCurrentPage('login');
          }}
        />
      );
    }
    return (
      <LoginPage
        onGoToRegister={() => {
          window.history.pushState({}, '', '/register');
          setCurrentPage('register');
        }}
      />
    );
  }

  // 로그인된 상태 - 관리자 페이지들
  // /login, /register 에서 로그인되면 원래 가려던 페이지로 이동
  if (isLoginPath || isRegisterPath) {
    const next = new URLSearchParams(location.search).get('next');
    if (next && next.startsWith('/')) {
      const nextRoute = adminAppRoutePath(next);
      if (
        isHqDashboardRole(currentUser.role) &&
        (nextRoute === '/login' || nextRoute === '/register' || nextRoute === '/')
      ) {
        return <Navigate to="/dashboard" replace />;
      }
      return <Navigate to={next} replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }

  if (isQuotesPath) {
    const goDashboard = () => {
      setCurrentPage('dashboard');
      navigate('/dashboard');
    };
    return (
      <>
        {currentUser && <AdminAlertsPopup currentUser={currentUser} />}
        <AdminLazyPageErrorBoundary title="Quotes & Contracts" onBack={goDashboard}>
          <Suspense fallback={<OrderRouteLoadingFallback />}>
            <QuotesContractsPage onBack={goDashboard} currentUser={currentUser} />
          </Suspense>
        </AdminLazyPageErrorBoundary>
      </>
    );
  }

  if (isReceiptAdminPath) {
    const goDashboard = () => {
      setCurrentPage('dashboard');
      navigate('/dashboard');
    };
    if (!currentUser || currentUser.role !== 'admin') {
      return <Navigate to="/dashboard" replace />;
    }
    return (
      <>
        {currentUser && <AdminAlertsPopup currentUser={currentUser} />}
        <AdminLazyPageErrorBoundary title="TheZone Receipt" onBack={goDashboard}>
          <Suspense fallback={<OrderRouteLoadingFallback />}>
            <TheZoneReceiptAdminPage onBack={goDashboard} currentUser={currentUser} />
          </Suspense>
        </AdminLazyPageErrorBoundary>
      </>
    );
  }

  if (isWeb2posSiteRoot) {
    return <Navigate to="/dashboard" replace />;
  }

  if (isWeb2posUtilityRoute || isLocalWeb2posDev) {
    if (normalizedAdminPath === '/download') {
      return (
        <Suspense fallback={<OrderRouteLoadingFallback />}>
          <Web2posDownloadPage showUpload={currentUser?.role === 'admin'} />
        </Suspense>
      );
    }
    if (normalizedAdminPath === '/upload') {
      if (currentUser?.role !== 'admin') {
        return <Navigate to="/download" replace />;
      }
      return (
        <Suspense fallback={<OrderRouteLoadingFallback />}>
          <Web2posUploadPage />
        </Suspense>
      );
    }
  }

  const handleNavigate = (page: string) => {
    if (page === 'deleted-restaurants') {
      setCurrentPage('restaurants');
      navigate('/deleted-restaurants');
      return;
    }
    setCurrentPage(page as PageType);
    if (page === 'quotes-contracts') {
      navigate('/quotes');
    } else if (page === 'thezone-receipt') {
      navigate('/thezone-receipt');
    } else if (page === 'restaurants') {
      navigate('/restaurants');
    } else if (page === 'customer-directory') {
      navigate('/restaurants/customer-directory');
    } else if (page === 'dashboard') {
      navigate('/dashboard');
    } else if (page === 'users' || page === 'billing') {
      navigate('/dashboard');
    }
  };

  const goDashboard = () => {
    setCurrentPage('dashboard');
    navigate('/dashboard');
  };

  const restaurantsInitialLifecycleTab = (() => {
    const path = location.pathname.replace(/\/$/, '') || '/';
    if (path === '/deleted-restaurants') return 'deleted' as const;
    const tab = new URLSearchParams(location.search).get('tab');
    if (
      tab === 'all' ||
      tab === 'active' ||
      tab === 'warning' ||
      tab === 'suspended' ||
      tab === 'deleted'
    ) {
      return tab;
    }
    return 'all' as const;
  })();

  // 레스토랑 선택 (메뉴 관리로 이동)
  const handleSelectRestaurant = (restaurant: Restaurant) => {
    if (currentUser?.restaurantRoles?.[restaurant.id] === 'accountant') {
      alert('This account has report-only access.');
      return;
    }
    const id = String(restaurant.id);
    stashRestaurantSubPageSnapshot({ ...restaurant, id });
    setSelectedRestaurant({ ...restaurant, id });
    setSelectedRestaurantId(id);
    setCurrentPage('menu-manage');
    navigate(restaurantAdminSubPath(id, 'menu'));
  };

  // 레스토랑 설정 페이지로 이동
  const handleOpenSettings = (restaurant: Restaurant) => {
    if (currentUser?.restaurantRoles?.[restaurant.id] === 'accountant') {
      alert('This account has report-only access.');
      return;
    }
    const id = String(restaurant.id);
    stashRestaurantSubPageSnapshot({ ...restaurant, id });
    setSelectedRestaurant({ ...restaurant, id });
    setSelectedRestaurantId(id);
    setCurrentPage('restaurant-settings');
    navigate(restaurantAdminSubPath(id, 'settings'));
    void (async () => {
      try {
        const full = await fetchFullRestaurant(id);
        if (full) setSelectedRestaurant({ ...full, id: String(full.id) });
      } catch {
        /* keep list snapshot */
      }
    })();
  };

  const restaurantSubPageFallback = (label: string) => (
    <div style={{ minHeight: '100vh', background: '#f8fafc', padding: 24 }}>
      <p style={{ color: '#64748b', marginBottom: 12 }}>Loading {label}…</p>
      <button type="button" onClick={() => handleBackToRestaurantModal(selectedRestaurantId || selectedRestaurant?.id)}>
        ← Restaurants
      </button>
    </div>
  );

  /** URL sub-routes drive the page unless user explicitly returned to Restaurants hub (Back). */
  const onRestaurantHub = currentPage === 'restaurants' || currentPage === 'deleted-restaurants';
  const pageFromRestaurantSubUrl =
    restaurantAdminSubRoute && firebaseUser && currentUser && !onRestaurantHub
      ? pageTypeForRestaurantAdminSub(restaurantAdminSubRoute.sub)
      : null;
  const pageToRender = pageFromRestaurantSubUrl ?? currentPage;

  return (
    <Suspense fallback={<OrderRouteLoadingFallback />}>
      {currentUser && <AdminAlertsPopup currentUser={currentUser} />}
      {(() => {
        switch (pageToRender) {
          case 'customer-portal':
            if (currentUser && isReceiptOnlyWebOwner(currentUser)) {
              return <ReceiptOwnerWebPage currentUser={currentUser} />;
            }
            return <CustomerPortalPage currentUser={currentUser} />;

          case 'dashboard':
            if (currentUser && isReceiptOnlyWebOwner(currentUser)) {
              return <ReceiptOwnerWebPage currentUser={currentUser} />;
            }
            if (
              currentUser &&
              currentUser.role !== 'admin' &&
              Object.keys(currentUser.restaurantRoles || {}).length > 0
            ) {
              return <CustomerPortalPage currentUser={currentUser} />;
            }
            return <DashboardPage onNavigate={handleNavigate} currentUser={currentUser} />;

          case 'restaurants':
          case 'deleted-restaurants':
            return (
              <RestaurantsPage
                onBack={goDashboard}
                onSelectRestaurant={handleSelectRestaurant}
                onOpenSettings={handleOpenSettings}
                onOpenActive={handleOpenActive}
                currentUser={currentUser}
                initialLifecycleTab={restaurantsInitialLifecycleTab}
                onOpenReport={(restaurantId: string) =>
                  handleOpenReport(
                    restaurantId,
                    currentPage === 'deleted-restaurants' ? 'deleted-restaurants' : 'restaurants',
                  )
                }
                onOpenGroupReport={(ids: string[], name: string) => {
                  setGroupReportIds(ids);
                  setGroupReportName(name);
                  setCurrentPage('group-report');
                }}
                onOpenCustomerDirectory={() => {
                  setCurrentPage('customer-directory');
                  navigate('/restaurants/customer-directory');
                }}
                onOpenBackOffice={(restaurant) => {
                  navigate(`/restaurant/${encodeURIComponent(String(restaurant.id))}/backoffice`);
                }}
              />
            );

          case 'restaurant-settings':
            if (selectedRestaurant && currentUser?.restaurantRoles?.[selectedRestaurant.id] === 'accountant') {
              return <Navigate to="/dashboard" replace />;
            }
            if (!selectedRestaurant) {
              return restaurantSubPageFallback('settings');
            }
            return (
              <AdminLazyPageErrorBoundary title="Restaurant Settings" onBack={() => handleBackToRestaurantModal()}>
                <Suspense fallback={<OrderRouteLoadingFallback />}>
                  <RestaurantSettingsPage
                    restaurant={selectedRestaurant}
                    onBack={() => handleBackToRestaurantModal()}
                    onOpenMenu={() => {
                      const id = String(selectedRestaurant.id);
                      setCurrentPage('menu-manage');
                      navigate(restaurantAdminSubPath(id, 'menu'));
                    }}
                    onUpdate={() => {
                      // 레스토랑 정보 업데이트 후 새로고침
                    }}
                    currentUser={currentUser}
                  />
                </Suspense>
              </AdminLazyPageErrorBoundary>
            );

          case 'restaurant-active':
            if (!selectedRestaurant?.id) {
              return (
                <div style={{ padding: 24, minHeight: '100vh', background: '#f8fafc' }}>
                  <p>No restaurant selected.</p>
                  <button type="button" onClick={() => handleBackToRestaurantModal()}>
                    ← Restaurants
                  </button>
                </div>
              );
            }
            return (
              <ActivatePageErrorBoundary onBack={() => handleBackToRestaurantModal()}>
                <Suspense fallback={<OrderRouteLoadingFallback />}>
                  <RestaurantActivePage
                    key={selectedRestaurant.id}
                    restaurant={selectedRestaurant}
                    currentUser={currentUser}
                    onRestaurantUpdated={handleRestaurantLifecycleUpdated}
                    onSoftDeleted={handleRestaurantSoftDeleted}
                    onBack={() => handleBackToRestaurantModal()}
                  />
                </Suspense>
              </ActivatePageErrorBoundary>
            );

          case 'menu-manage':
            if (selectedRestaurant && currentUser?.restaurantRoles?.[selectedRestaurant.id] === 'accountant') {
              return <Navigate to="/dashboard" replace />;
            }
            if (!selectedRestaurant) {
              return restaurantSubPageFallback('menu');
            }
            return (
              <AdminLazyPageErrorBoundary title="Menu" onBack={() => handleBackToRestaurantModal()}>
                <Suspense fallback={<OrderRouteLoadingFallback />}>
                  <RestaurantTabsPage
                    restaurant={selectedRestaurant}
                    onBack={() => handleBackToRestaurantModal()}
                    onOpenSettings={() => {
                      const id = String(selectedRestaurant.id);
                      setCurrentPage('restaurant-settings');
                      navigate(restaurantAdminSubPath(id, 'settings'));
                    }}
                    currentUser={currentUser}
                  />
                </Suspense>
              </AdminLazyPageErrorBoundary>
            );

          case 'users':
            return (
              <UsersPage
                onBack={() => setCurrentPage('dashboard')}
                currentUser={currentUser}
                onSelectRestaurant={handleSelectRestaurant}
                onOpenSettings={handleOpenSettings}
                onOpenReport={(restaurantId: string) => handleOpenReport(restaurantId, 'users')}
                onOpenActive={handleOpenActive}
              />
            );

          case 'billing':
            return (
              <BillingPage
                onBack={() => setCurrentPage('dashboard')}
                currentUser={currentUser}
              />
            );

          case 'quotes-contracts':
            return (
              <QuotesContractsPage
                onBack={() => {
                  setCurrentPage('dashboard');
                  navigate('/dashboard');
                }}
                currentUser={currentUser}
              />
            );

          case 'thezone-receipt':
            if (currentUser?.role !== 'admin') {
              return <Navigate to="/dashboard" replace />;
            }
            return (
              <TheZoneReceiptAdminPage
                onBack={() => {
                  setCurrentPage('dashboard');
                  navigate('/dashboard');
                }}
                currentUser={currentUser}
              />
            );

          case 'customer-directory': {
            const role = currentUser?.role;
            if (role !== 'admin' && role !== 'distributor' && role !== 'dealer') {
              return <Navigate to="/dashboard" replace />;
            }
            return (
              <RestaurantCustomerDirectoryPage
                onBack={() => {
                  setCurrentPage('restaurants');
                  navigate('/restaurants');
                }}
                currentUser={currentUser}
              />
            );
          }

          case 'restaurant-report':
            if (!selectedRestaurantId) {
              return restaurantSubPageFallback('report');
            }
            return (
              <AdminLazyPageErrorBoundary
                title="Report"
                onBack={() => handleBackToRestaurantModal(selectedRestaurantId)}
              >
                <NewReportPage
                  restaurantIdProp={selectedRestaurantId}
                  onBack={() => handleBackToRestaurantModal(selectedRestaurantId)}
                />
              </AdminLazyPageErrorBoundary>
            );

          case 'group-report':
            return (
              <GroupReportPage
                restaurantIds={groupReportIds}
                groupName={groupReportName}
                onBack={() => setCurrentPage('restaurants')}
              />
            );

          default:
            return (
              <DashboardPage
                onNavigate={handleNavigate}
                currentUser={currentUser}
              />
            );
        }
      })()}
    </Suspense>
  );
}

export default App;
